import { z } from "zod";

/**
 * Storage hierarchy kinds, parent → child: Warehouse → Location → Bin → Sub-bin. A warehouse is the
 * root (DEORJHAL); VNR writes the location as "WH-01".
 *
 * A node's `path` starts at its LOCATION, not the warehouse: DEORJHAL's sub-bin reads "WH-01/A01/3".
 * The warehouse is reached through `parent_id` only, so every SLOC display (form columns WH | Bin |
 * Sub Bin, summaries, pickers) shows the three segments operators use. A path is unique per plant.
 */
export const LOCATION_KINDS = [
  "warehouse",
  "location",
  "bin",
  "sub_bin",
] as const;
export type LocationKind = (typeof LOCATION_KINDS)[number];
export const LOCATION_CHILD_KIND: Record<LocationKind, LocationKind | null> = {
  warehouse: "location",
  location: "bin",
  bin: "sub_bin",
  sub_bin: null,
};

/**
 * Which department a warehouse, bin or sub-bin is allotted to (user, 2026-10-10): the seed side's
 * lots and the Stores module's items keep separate racks, so each sees only its own in its pickers,
 * and neither can put stock into the other's. Set on a WH and every bin under it follows; a bin or
 * sub-bin may be allotted on its own. Unset all the way up means Seed.
 */
export const STORAGE_DEPARTMENTS = ["seed", "stores"] as const;
export type StorageDepartment = (typeof STORAGE_DEPARTMENTS)[number];
export const STORAGE_DEPARTMENT_LABEL: Record<StorageDepartment, string> = {
  seed: "Seed",
  stores: "Stores",
};
/** The department a node is in: its own allotment, else the nearest ancestor's, else Seed. */
export function effectiveDepartment(
  chain: readonly { department?: StorageDepartment | null }[]
): StorageDepartment {
  for (const n of chain) if (n.department) return n.department;
  return "seed";
}

/**
 * A warehouse's storage type, set on the root (user, 2026-10-18 added Store). A Store-type tree is
 * the Stores module's own: the whole tree is allotted to Stores and no node in it may be Seed.
 */
export const STORAGE_TYPES = ["general", "cold", "drying", "store"] as const;
export type StorageType = (typeof STORAGE_TYPES)[number];
export const STORAGE_TYPE_LABEL: Record<StorageType, string> = {
  general: "General",
  cold: "Cold",
  drying: "Drying facility",
  store: "Store",
};

const qty = z.union([z.number(), z.string()]).transform((v) => String(v));

export const locationNode = {
  create: z.object({
    facilityId: z.number().int(),
    kind: z.enum(LOCATION_KINDS),
    parentId: z.number().int().nullable(),
    code: z.string().min(1).max(30),
    name: z.string().max(100).nullish(),
    capacityKg: qty.optional(),
    isQuarantine: z.boolean().optional(),
    ownership: z.enum(["internal", "external"]).nullish(),
    storageType: z.enum(STORAGE_TYPES).nullish(),
    shortName: z.string().max(10).nullish(),
    department: z.enum(STORAGE_DEPARTMENTS).nullish(),
  }),
  update: z.object({
    name: z.string().max(100).nullish(),
    capacityKg: qty.optional(),
    isQuarantine: z.boolean().optional(),
    isActive: z.boolean().optional(),
    ownership: z.enum(["internal", "external"]).nullish(),
    storageType: z.enum(STORAGE_TYPES).nullish(),
    shortName: z.string().max(10).nullish(),
    /** Null puts the node back on its parent's allotment. */
    department: z.enum(STORAGE_DEPARTMENTS).nullish(),
  }),
};

/**
 * Copy a warehouse tree under a new root (Masters ▸ Locations "Copy as…", user 2026-10-18). Paths
 * are unique per plant and leave out the root, so every copied WH code gets `prefix`
 * (WH-01 → ST-WH-01); bin and sub-bin codes stay.
 */
export const locationCopy = {
  body: z.object({
    code: z.string().trim().min(1).max(30),
    name: z.string().trim().max(100).nullish(),
    prefix: z
      .string()
      .trim()
      .toUpperCase()
      .regex(
        /^[A-Z0-9]{1,5}-$/,
        "Use 1–5 letters or digits and a dash, e.g. ST-"
      ),
    storageType: z.enum(STORAGE_TYPES).default("store"),
    includeSubBins: z.boolean().default(true),
    dryRun: z.boolean().default(true),
  }),
};

/** Bin-master sheet import: one row per location with its bin code list ("A1 To A5, B7"). */
export const binMasterImport = {
  body: z.object({
    facilityId: z.number().int(),
    warehouseCode: z.string().min(1).max(30),
    rows: z
      .array(
        z.object({
          location: z.string(),
          bins: z.string(),
          name: z.string().max(100).optional(),
        })
      )
      .min(1)
      .max(2000),
    subBins: z.number().int().min(0).max(100).default(20),
    dryRun: z.boolean().default(true),
  }),
};
